import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Sparkles, Loader2, ShieldCheck, AlertTriangle, ArrowRight, ScanLine } from "lucide-react";
import { decodeSingleClause, type SingleClauseResult } from "@/lib/single-clause.functions";
import { useAuth } from "@/hooks/use-auth";

const SAMPLES = [
  "Tenant shall be solely responsible for any and all repairs, regardless of cause, including those resulting from ordinary wear and tear or pre-existing conditions.",
  "Contractor irrevocably assigns all intellectual property, including pre-existing works and future ideas, to Company in perpetuity without additional compensation.",
  "Either party may terminate this Agreement at any time, for any reason, with no notice and no further obligation.",
];

export function SingleClauseDecoder() {
  const { user } = useAuth();
  const decode = useServerFn(decodeSingleClause);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SingleClauseResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleDecode() {
    setError(null);
    setResult(null);
    const trimmed = text.trim();
    if (trimmed.length < 8) {
      setError("Paste a sentence from your contract to decode.");
      return;
    }
    setLoading(true);
    try {
      const res = await decode({ data: { text: trimmed } });
      setResult(res);
      if (!res.ok) {
        if (res.reason === "rate_limited") setError("We're a bit busy — try again in a moment.");
        else if (res.reason === "ai_credits_exhausted")
          setError("Decoder is recharging. Please try again soon.");
        else setError("Couldn't decode that. Try a different sentence.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const decoded = result && result.ok ? result : null;

  return (
    <div
      className="mx-auto mt-10 w-full max-w-3xl rounded-2xl border bg-white p-5 text-left shadow-sm sm:p-6"
      style={{ borderColor: "#E5E7EB" }}
    >
      <div className="flex items-center gap-2">
        <span
          className="inline-flex h-9 w-9 items-center justify-center rounded-full"
          style={{ background: "rgba(27,58,107,0.08)", color: "#1B3A6B" }}
        >
          <Sparkles className="h-5 w-5" />
        </span>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
            Single Clause Decoder
          </div>
          <div style={{ fontSize: 13, color: "#6B7280" }}>
            Paste one confusing sentence — get plain English + a risk score. No sign-up needed.
          </div>
        </div>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder='e.g. "Tenant waives all right to a jury trial in any dispute arising from this Lease…"'
        rows={4}
        maxLength={1500}
        className="mt-4 w-full resize-y rounded-xl border bg-white p-3 text-sm outline-none transition focus:ring-2"
        style={{
          borderColor: "#E5E7EB",
          color: "#111827",
        }}
      />

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs" style={{ color: "#6B7280" }}>
          <span>Try:</span>
          {SAMPLES.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setText(s)}
              className="rounded-full border px-2 py-1 transition hover:border-slate-400"
              style={{ borderColor: "#E5E7EB", color: "#1B3A6B" }}
            >
              Sample {i + 1}
            </button>
          ))}
        </div>
        <div className="text-xs" style={{ color: "#9CA3AF" }}>
          {text.length}/1500
        </div>
      </div>

      <button
        type="button"
        onClick={handleDecode}
        disabled={loading}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white transition disabled:opacity-70 sm:w-auto"
        style={{ background: "#1B3A6B" }}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Decoding…
          </>
        ) : (
          <>
            <ScanLine className="h-4 w-4" /> Decode this clause
          </>
        )}
      </button>

      {error && (
        <div
          className="mt-3 rounded-lg border px-3 py-2 text-sm"
          style={{ borderColor: "#FECACA", background: "#FEF2F2", color: "#991B1B" }}
        >
          {error}
        </div>
      )}

      {decoded && (
        <div className="mt-5 space-y-4">
          {decoded.notClause ? (
            <div
              className="rounded-xl border p-4 text-sm"
              style={{ borderColor: "#FDE68A", background: "#FFFBEB", color: "#92400E" }}
            >
              {decoded.plainEnglish ||
                "That didn't look like a legal clause. Try pasting a sentence from a contract."}
            </div>
          ) : (
            <>
              <RiskMeter score={decoded.riskScore} level={decoded.riskLevel} />
              <div>
                <div
                  className="mb-1 text-xs font-semibold uppercase tracking-wide"
                  style={{ color: "#6B7280" }}
                >
                  Plain English
                </div>
                <p className="text-sm leading-relaxed" style={{ color: "#111827" }}>
                  {decoded.plainEnglish}
                </p>
              </div>
              {decoded.watchOut && (
                <div
                  className="flex gap-2 rounded-xl border p-3 text-sm"
                  style={{
                    borderColor: decoded.riskLevel === "high" ? "#FECACA" : "#FDE68A",
                    background: decoded.riskLevel === "high" ? "#FEF2F2" : "#FFFBEB",
                    color: decoded.riskLevel === "high" ? "#991B1B" : "#92400E",
                  }}
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{decoded.watchOut}</span>
                </div>
              )}

              {!user && (
                <div
                  className="rounded-xl border p-4"
                  style={{ borderColor: "#BFDBFE", background: "#EFF6FF" }}
                >
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" style={{ color: "#1B3A6B" }} />
                    <div className="flex-1">
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#1B3A6B" }}>
                        Scan the whole document — free
                      </div>
                      <div className="mt-1 text-sm" style={{ color: "#374151" }}>
                        Sign up and get <strong>2 free trial credits</strong> to analyze your full
                        contract, with red flags, key terms, and next steps.
                      </div>
                      <Link
                        to="/auth"
                        className="mt-3 inline-flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-semibold text-white transition"
                        style={{ background: "#1B3A6B" }}
                      >
                        Claim 2 free credits <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-[11px]" style={{ color: "#9CA3AF" }}>
                NOT LEGAL ADVICE. For informational purposes only.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RiskMeter({ score, level }: { score: number; level: "low" | "medium" | "high" }) {
  const palette =
    level === "high"
      ? { bg: "#FEF2F2", bar: "#DC2626", text: "#991B1B", label: "High risk" }
      : level === "medium"
        ? { bg: "#FFFBEB", bar: "#F59E0B", text: "#92400E", label: "Medium risk" }
        : { bg: "#ECFDF5", bar: "#10B981", text: "#065F46", label: "Low risk" };
  return (
    <div className="rounded-xl p-3" style={{ background: palette.bg }}>
      <div
        className="flex items-center justify-between text-xs font-semibold"
        style={{ color: palette.text }}
      >
        <span>{palette.label}</span>
        <span>{score}/100</span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/70">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${score}%`, background: palette.bar }}
        />
      </div>
    </div>
  );
}
